import React, { useState, useEffect, useRef } from 'react'
import { gql, useMutation } from '@apollo/client'
import { DateTime } from 'luxon'
import { useHistory, useLocation } from 'react-router-dom'
import { useAuth } from 'lib/auth'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { isEmpty, get } from 'lodash'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import { MarkdownRteFlushProvider } from 'lib/rte-utils/markdown-rte-flush-context'
import {
  Grid,
  Box,
  CardHeader,
  CardContent,
  Card,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core'
import { gtmEvent } from 'lib/gtm'
import {
  trackVirtualCoachingEvent,
  VIRTUAL_COACHING_EVENTS,
} from 'pages/pga-coach/virtual-coaching/analytics'
import { trackLessonPackCreatedAfterHolidayBanner } from 'pages/pga-coach/dashboard/holiday-lesson-pack-banner/analytics'
import { trackBookingTypeCreated } from 'pages/pga-coach/bookings/booking-types/analytics'
import { dollarsToCents, centsToDollars } from 'lib/utils/number'
import useIconAccessor from 'components/lesson-type-category/icons'
import BookingTypeFormSuccessDialog from './booking-type-form-success-dialog'
import EditBookingTypeLayout from './edit-booking-type-layout'
import { getFirstErrorMessage } from 'pages/pga-coach/bookings/events/form-validation-utils'
import useStyles from './styles'
import Dialog from 'components/dialog'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import { expirationDurationOptions } from 'pages/pga-coach/bookings/booking-types/components/expiration-duration-dropdown'
import SingleOrLessonPack from 'pages/pga-coach/bookings/booking-types/components/single-or-lesson-packs'
import BookingTypeDetails from 'pages/pga-coach/bookings/booking-types/components/booking-type-details'
import BookingTypePaymentOptions from 'pages/pga-coach/bookings/booking-types/components/booking-type-payment-options'
import BookingTypeAdditionalOptions from 'pages/pga-coach/bookings/booking-types/components/booking-type-additional-options'
import { inputPriceRegExp } from 'utils/regExp'
import { PageHeader } from 'components/page-header'
import OnboardingProgressSection from 'components/onboarding-progress'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import { FormActionBar } from 'components/form-util'
import { useScrollToHash } from 'lib/hooks'
import {
  getOfferingsPath,
  OFFERINGS_TAB,
} from 'pages/pga-coach/bookings/offerings/routes'
import { useVirtualCoachingEnabled } from 'lib/pga-coach/use-virtual-coaching'
import {
  DEFAULT_TURNAROUND_HOURS,
  endOfDayInZone,
  expiresAtToDate,
  getOfferingZone,
  isVirtualCoaching as isVirtualCoachingOffering,
  OFFERING_KIND,
  TURNAROUND_HOURS_OPTIONS,
  VIRTUAL_COACHING_CATEGORY,
} from 'pages/pga-coach/bookings/booking-types/offering-kind'

const hasDate = value => DateTime.isDateTime(value) && value.isValid

const CREATE_LESSON_TYPE = gql`
  mutation CreateLessonType($input: CreateLessonTypeInput!) {
    createLessonType(input: $input) {
      success
      message
      lessonType {
        id
        slug
        category
        accessCode
        giftingEnabled
        academy {
          id
        }
        coach {
          externalId
          coachProfile {
            slug
          }
        }
        facilities {
          id
        }
        lessonPackOfferings {
          id
        }
      }
    }
  }
`

const UPDATE_LESSON_TYPE = gql`
  mutation UpdateLessonType($input: UpdateLessonTypeInput!) {
    updateLessonType(input: $input) {
      success
      message
      lessonType {
        slug
        giftingEnabled
        coach {
          externalId
          coachProfile {
            slug
          }
        }
        academy {
          id
        }
      }
    }
  }
`

const DELETE_LESSON_TYPE = gql`
  mutation DeleteLessonType($id: ID!) {
    deleteLessonType(id: $id) {
      success
      message
    }
  }
`

const durationInMinutesOptions = [
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '45 minutes', value: 45 },
  { label: '50 minutes', value: 50 },
  { label: '60 minutes', value: 60 },
  { label: '90 minutes', value: 90 },
  { label: '2 hours', value: 120 },
  { label: '3 hours', value: 180 },
  { label: '4 hours', value: 240 },
  { label: '5 hours', value: 300 },
  { label: '6 hours', value: 360 },
  { label: '7 hours', value: 420 },
  { label: '8 hours', value: 480 },
  { label: '9 hours', value: 540 },
]

const facilityOptions = (coachFacilities, coachingCenters) => [
  ...coachFacilities,
  ...coachingCenters.flatMap(center => center.facilities ?? []),
]

const validationSchema = (
  categories,
  coachFacilities,
  coachingCenters,
  paymentAccounts,
  savedExpiresAt,
) =>
  yup.object().shape({
    isLessonPacks: yup.boolean().required(),
    isVirtualCoaching: yup.boolean(),
    category: yup
      .string()
      .oneOf(categories.map(bc => bc.value))
      .when('isVirtualCoaching', (isVirtualCoaching, field) =>
        isVirtualCoaching ? yup.string().nullable() : field,
      ),
    title: yup.string().required(),
    location: yup
      .string()
      .required()
      .when('isPrivateBookingEnabled', (isPrivateBookingEnabled, field) => {
        isPrivateBookingEnabled
          ? field.oneOf([
            ...coachFacilities.map(cf => cf.id),
            ...coachingCenters.map(cc => cc.id),
          ])
          : field.oneOf(coachFacilities.map(cf => cf.id))
      }),
    coachingCenter: yup.string(),
    durationInMinutes: yup
      .number()
      .oneOf(durationInMinutesOptions.map(dim => dim.value))
      .required()
      .when('isVirtualCoaching', (isVirtualCoaching, field) =>
        isVirtualCoaching ? yup.number().nullable() : field,
      ),
    startTimeIncrementInMinutes: yup
      .number()
      .required()
      .when('isVirtualCoaching', (isVirtualCoaching, field) =>
        isVirtualCoaching ? yup.number().nullable() : field,
      ),
    turnaroundHours: yup.number().when('isVirtualCoaching', {
      is: true,
      then: yup
        .number()
        .oneOf(TURNAROUND_HOURS_OPTIONS.map(option => option.value))
        .required(),
    }),
    purchaseLimit: yup
      .string()
      .nullable()
      .matches(/^[1-9][0-9]*$/, {
        message: 'Enter a whole number greater than 0',
        excludeEmptyString: true,
      }),
    purchaseLimitPeriod: yup.string().nullable(),
    expirationDuration: yup
      .string()
      .oneOf(expirationDurationOptions.map(dim => dim.value)),
    expiresAt: yup
      .mixed()
      .nullable()
      .test('valid-date', 'Enter a valid date', value =>
        value == null ? true : hasDate(value),
      )
      .test('not-in-past', 'Choose today or a later date', function (value) {
        if (!hasDate(value)) return true
        // An offering whose date has passed stays editable: only a changed
        // date has to be today or later.
        if (savedExpiresAt && value.hasSame(savedExpiresAt, 'day')) return true
        const timezone = getOfferingZone(
          facilityOptions(coachFacilities, coachingCenters),
          this.parent.location,
        )
        return endOfDayInZone(value, timezone) >= DateTime.local()
      }),
    price: yup
      .string()
      .matches(inputPriceRegExp, 'Please enter a valid price')
      .when('isVirtualCoaching', (isVirtualCoaching, field) =>
        isVirtualCoaching
          ? field
            .required('Enter a price')
            .test(
              'greater-than-zero',
              'Price must be greater than $0',
              value => Number((value || '').replace(/,/g, '')) > 0,
            )
          : field,
      ),
    paymentAccountId: yup
      .string()
      .oneOf([
        '',
        ...paymentAccounts
          .filter(pa => pa.detailsSubmitted && pa.chargesEnabled)
          .map(pa => pa.id),
      ])
      .nullable()
      .when(
        ['isLessonPacks', 'isVirtualCoaching'],
        (isLessonPacks, isVirtualCoaching, field) =>
          isLessonPacks || isVirtualCoaching
            ? field.required('Select a payment account')
            : field,
      ),
    description: yup.string().nullable(),
    confirmationMessage: yup.string().nullable(),
    lessonPacks: yup.array().when('isLessonPacks', {
      is: true,
      then: yup.array().of(
        yup.object().shape({
          quantity: yup
            .string()
            .matches(/^[1-9]([0-9]+)?$/, 'Please enter a valid quantity')
            .label('Quantity')
            .required(),
          priceInCents: yup
            .string()
            .matches(inputPriceRegExp, 'Please enter a valid price')
            .label('Price')
            .required('Please enter a valid price'),
        }),
      ),
    }),
    accessCode: yup.string().matches(/^[a-z0-9]+$/i, {
      message: 'Codes must consist only of numbers and letters with no spaces',
      excludeEmptyString: true,
    }),
    giftingEnabled: yup.boolean(),
  })

const GENERIC_ERROR_MESSAGE = 'An error has occurred. Please try again later.'

export const getMutationErrorMessage = error =>
  error?.graphQLErrors?.[0]?.message || GENERIC_ERROR_MESSAGE

const onSave = () => {
  // GTM Tracking Code
  gtmEvent({
    event: 'formSubmit',
    formCategory: 'bookings',
    formAction: 'update-booking-type',
    formStatus: 'success',
  })
}

const onCreate = user => {
  // GTM Tracking Code
  if (user.onboarding) {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'bookings',
      formAction: 'create-onboarding-booking-type',
      formStatus: 'success',
    })
  } else {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'bookings',
      formAction: 'create-booking-type',
      formStatus: 'success',
    })
  }
}

const onDelete = () => {
  // GTM Tracking Code
  gtmEvent({
    event: 'formSubmit',
    formCategory: 'bookings',
    formAction: 'delete-booking-type',
    formStatus: 'success',
  })
}

const BookingTypeForm = ({
  bookingType,
  categories,
  coachFacilities,
  coachAvailabilities,
  coachingCenters,
  paymentAccounts,
  startTimeIncrementOptions,
  onSuccess,
  coachOnboardingProgress,
  showOnboardingModal,
  isOnboarding,
}) => {
  const { user } = useAuth()
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const history = useHistory()
  const { search } = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const [isHolidayLessonPackBannerEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_HOLIDAY_LESSON_PACK_BANNER,
  ])
  useScrollToHash({ isReady: true, delay: 100 })

  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false)
  const [lessonTypeSuccessResult, setLessonTypeSuccessResult] = useState(null)
  const [status, setStatus] = useState(bookingType?.status || 'ACTIVE')
  const [giftingEnabled, setGiftingEnabled] = useState(
    bookingType?.giftingEnabled || false,
  )
  const initialStatus = bookingType?.status || 'ACTIVE'
  const initialGiftingEnabled = Boolean(bookingType?.giftingEnabled)
  const singleLessonPrice = bookingType && bookingType.priceInCents

  const startsAsLessonPack =
    bookingType?.lessonPacks?.length > 0 ||
    (!bookingType &&
      new URLSearchParams(search).get('offering') === 'lesson-pack')
  const [isLessonPacks, setIsLessonPacks] = useState(startsAsLessonPack)
  const virtualCoachingEnabled = useVirtualCoachingEnabled()
  const [isVirtualCoaching, setIsVirtualCoaching] = useState(
    isVirtualCoachingOffering(bookingType),
  )

  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const accessor = useIconAccessor()

  const defaultStartTimeIncrementInMinutes =
    startTimeIncrementOptions.find(option => option.default)?.value ??
    startTimeIncrementOptions[0]?.value

  const savedExpiresAtIso = bookingType?.virtualCoaching?.expiresAt
  const savedExpiresAt = savedExpiresAtIso
    ? expiresAtToDate(
      savedExpiresAtIso,
      getOfferingZone(
        facilityOptions(coachFacilities, coachingCenters),
        get(bookingType, 'locations[0].id'),
      ),
    )
    : null

  const initialValues =
    bookingType != null
      ? {
        ...bookingType,
        description: bookingType?.description ?? '',
        confirmationMessage: bookingType?.confirmationMessage ?? '',
        accessCode: bookingType?.accessCode || '',
        location: get(bookingType, 'locations[0].id'),
        coachingCenter: get(bookingType, 'coachingCenter.id' || null),
        price: centsToDollars(bookingType.priceInCents) ?? '',
        paymentAccountId: bookingType?.paymentAccount?.id || '',
        startTimeIncrementInMinutes:
          bookingType.startTimeIncrementInMinutes ??
          defaultStartTimeIncrementInMinutes,
        expirationDuration:
          bookingType?.lessonPacks[0]?.expirationDuration ||
          expirationDurationOptions.find(opt => opt.default).value,
        giftingEnabled: bookingType?.giftingEnabled || false,
        turnaroundHours:
          bookingType?.virtualCoaching?.turnaroundHours ??
          DEFAULT_TURNAROUND_HOURS,
        purchaseLimit: bookingType?.virtualCoaching?.purchaseLimit
          ? String(bookingType.virtualCoaching.purchaseLimit)
          : '',
        purchaseLimitPeriod:
          bookingType?.virtualCoaching?.purchaseLimitPeriod || 'WEEK',
        expiresAt: savedExpiresAt,
      }
      : {
        category: '',
        description: '',
        confirmationMessage: '',
        location: '',
        paymentAccountId: '',
        lessonPacks: [
          {
            quantity: '',
            pricePerLesson: '',
            priceInCents: '',
          },
        ],
        expirationDuration: expirationDurationOptions.find(opt => opt.default)
          .value,
        giftingEnabled: false,
        turnaroundHours: DEFAULT_TURNAROUND_HOURS,
        purchaseLimit: '',
        purchaseLimitPeriod: 'WEEK',
        expiresAt: null,
      }

  const defaultValues = {
    ...initialValues,
    isLessonPacks: startsAsLessonPack,
    isVirtualCoaching: isVirtualCoachingOffering(bookingType),
  }

  const validationErrorHandlerRef = useRef(() => {})
  const markdownRteFlushApiRef = useRef(null)
  const isEditFlow = Boolean(bookingType) && !isOnboarding
  const useSectionLayout = !bookingType || isEditFlow

  const [
    createLessonType,
    { loading: isCreateLessonTypeSubmitting },
  ] = useMutation(CREATE_LESSON_TYPE, {
    onCompleted: data => {
      if (data.createLessonType.success) {
        enqueueSnackbar(data.createLessonType.message, {
          variant: 'success',
        })
        if (isVirtualCoaching) {
          trackVirtualCoachingEvent(VIRTUAL_COACHING_EVENTS.OFFERING_CREATED, {
            lessonTypeId: data.createLessonType.lessonType?.id,
          })
        }
        if (!user?.isAssumed && !isVirtualCoaching) {
          trackBookingTypeCreated({
            salesModel: isLessonPacks ? 'pack' : 'single',
            category: data.createLessonType.lessonType?.category,
          })
        }
        if (
          isLessonPacks &&
          isHolidayLessonPackBannerEnabled &&
          user?.id &&
          !user.isAssumed
        ) {
          trackLessonPackCreatedAfterHolidayBanner({
            userId: user.id,
            lessonTypeId: data.createLessonType.lessonType?.id,
            lessonPackCount:
              data.createLessonType.lessonType?.lessonPackOfferings?.length ??
              0,
          })
        }
        setLessonTypeSuccessResult(data.createLessonType.lessonType)
        setIsSuccessDialogOpen(true)
        onCreate(user)
      } else {
        enqueueSnackbar(data.createLessonType.message, { variant: 'error' })
      }
    },
    onError: error => {
      window.rg4js &&
        window.rg4js('send', {
          error: error,
          tags: ['booking-types'],
        })
      enqueueSnackbar(getMutationErrorMessage(error), {
        variant: 'error',
      })
    },
  })
  const [
    updateLessonType,
    { loading: isUpdateLessonTypeSubmitting },
  ] = useMutation(UPDATE_LESSON_TYPE, {
    onCompleted: data => {
      if (data.updateLessonType.success) {
        enqueueSnackbar(data.updateLessonType.message, {
          variant: 'success',
        })
        setLessonTypeSuccessResult(data.updateLessonType.lessonType)
        onSave()
        history.push(getOfferingsPath(OFFERINGS_TAB.BOOKING_TYPE))
      } else {
        enqueueSnackbar(data.updateLessonType.message, { variant: 'error' })
      }
    },
    onError: error => {
      window.rg4js &&
        window.rg4js('send', {
          error: error,
          tags: ['booking-types'],
        })
      enqueueSnackbar(getMutationErrorMessage(error), {
        variant: 'error',
      })
    },
  })

  const [deleteLessonType] = useMutation(DELETE_LESSON_TYPE, {
    onCompleted: data => {
      if (data.deleteLessonType.success) {
        enqueueSnackbar('Booking Type has been successfully deleted.', {
          variant: 'success',
        })
        onDelete()
        history.push(getOfferingsPath(OFFERINGS_TAB.BOOKING_TYPE))
      } else {
        enqueueSnackbar(data.deleteLessonType.message, { variant: 'error' })
      }
    },
    onError: error => {
      window.rg4js &&
        window.rg4js('send', {
          error: error,
          tags: ['booking-types'],
        })
      enqueueSnackbar('An error has occurred. Please try again later.', {
        variant: 'error',
      })
    },
  })

  const form = useForm({
    defaultValues,
    shouldUnregister: false,
    mode: useSectionLayout ? 'onChange' : 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      validationSchema(
        categories,
        coachFacilities,
        coachingCenters,
        paymentAccounts,
        savedExpiresAt,
      ),
    ),
  })
  const {
    register,
    handleSubmit,
    errors,
    control,
    setValue,
    getValues,
    formState,
    watch,
    trigger,
  } = form
  const { touched, isValid, isDirty } = formState
  const hasErrors = Object.keys(errors).length > 0
  const isFormDirty =
    isDirty ||
    (Boolean(bookingType) &&
      (status !== initialStatus || giftingEnabled !== initialGiftingEnabled))

  const flushMarkdownEditorsAndSubmit = (onValid, onInvalid) => event => {
    markdownRteFlushApiRef.current?.flushAll()
    return handleSubmit(onValid, onInvalid)(event)
  }

  useEffect(() => {
    setValue('isLessonPacks', isLessonPacks, { shouldValidate: false })
  }, [isLessonPacks, setValue])

  useEffect(() => {
    setValue('isVirtualCoaching', isVirtualCoaching, { shouldValidate: false })
  }, [isVirtualCoaching, setValue])

  useEffect(() => {
    if (!isLessonPacks) return

    const currentLessonPacks = getValues('lessonPacks')
    if (Array.isArray(currentLessonPacks) && currentLessonPacks.length > 0) {
      return
    }

    setValue(
      'lessonPacks',
      [
        {
          quantity: '',
          pricePerLesson: '',
          priceInCents: '',
        },
      ],
      { shouldValidate: false },
    )
  }, [getValues, isLessonPacks, setValue])

  const handleCategoryChange = e => {
    const suggestedName = accessor[e.target.value].suggestedName
    if (suggestedName != null) {
      setValue('title', suggestedName, {
        shouldDirty: true,
      })
    }
  }

  const onSuccessDialogClose = () => {
    onSuccess && onSuccess()
    setIsSuccessDialogOpen(false)
    history.push(getOfferingsPath(OFFERINGS_TAB.BOOKING_TYPE))
  }

  const onSuccessDialogCreateAnother = () => {
    onSuccess && onSuccess()
    setIsSuccessDialogOpen(false)
    history.push('/pga-coach/bookings/booking-types/new')
  }

  const onDeleteBookingType = () => {
    deleteLessonType({
      variables: {
        id: bookingType.id,
      },
    })
  }

  const onSubmit = data => {
    const purchaseLimit = Number(data.purchaseLimit)
    const input = {
      title: data.title,
      category: data.category,
      description: data.description,
      confirmationMessage: data.confirmationMessage,
      locationIds: [data.location],
      ...(bookingType == null && {
        academyId: data.coachingCenter || null,
      }),
      ...(isLessonPacks
        ? {
          lessonPacks: data.lessonPacks.map(lp => ({
            quantity: +lp.quantity,
            priceInCents: dollarsToCents(lp.priceInCents),
            expirationDuration:
              data.expirationDuration === 'never'
                ? null
                : data.expirationDuration,
          })),
        }
        : {
          priceInCents: dollarsToCents(data.price),
        }),
      ...{
        paymentAccountId:
          (data.paymentAccountId || '').length > 0
            ? data.paymentAccountId
            : null,
      },
      accessCode: data.accessCode,
      status: status,
      giftingEnabled: giftingEnabled,
      ...(isVirtualCoaching
        ? {
          offeringKind: OFFERING_KIND.VIRTUAL_COACHING,
          category: VIRTUAL_COACHING_CATEGORY,
          virtualCoaching: {
            turnaroundHours: Number(data.turnaroundHours),
            purchaseLimit: purchaseLimit || null,
            purchaseLimitPeriod: purchaseLimit
              ? data.purchaseLimitPeriod
              : null,
            expiresAt: hasDate(data.expiresAt)
              ? endOfDayInZone(
                data.expiresAt,
                getOfferingZone(
                  facilityOptions(coachFacilities, coachingCenters),
                  data.location,
                ),
              ).toISO()
              : null,
          },
        }
        : {
          durationInMinutes: data.durationInMinutes,
          startTimeIncrementInMinutes: data.startTimeIncrementInMinutes,
        }),
    }
    if (bookingType) {
      updateLessonType({
        variables: {
          input: {
            id: bookingType.id,
            ...input,
          },
        },
      })
    } else {
      createLessonType({
        variables: { input },
      })
    }
  }
  const crumbs = [
    isOnboarding
      ? { label: 'Onboarding Dashboard', to: '/pga-coach' }
      : {
        label: 'My Offerings',
        to: getOfferingsPath(OFFERINGS_TAB.BOOKING_TYPE),
      },
  ]

  const isCreateFlow = !bookingType
  const isSubmitting = isCreateFlow
    ? isCreateLessonTypeSubmitting
    : isUpdateLessonTypeSubmitting

  const handleCancel = () => {
    history.push(
      isOnboarding
        ? '/pga-coach'
        : getOfferingsPath(OFFERINGS_TAB.BOOKING_TYPE),
    )
  }

  if (useSectionLayout) {
    return (
      <>
        <MarkdownRteFlushProvider flushApiRef={markdownRteFlushApiRef}>
          <FormProvider {...form}>
            <form
              onSubmit={flushMarkdownEditorsAndSubmit(onSubmit, formErrors => {
                validationErrorHandlerRef.current(formErrors)
                const errorMessage = getFirstErrorMessage(formErrors)
                if (errorMessage) {
                  enqueueSnackbar(errorMessage, { variant: 'error' })
                }
              })}
            >
              <EditBookingTypeLayout
                history={history}
                bookingType={bookingType}
                isCreateFlow={isCreateFlow}
                isLessonPacks={isLessonPacks}
                setIsLessonPacks={setIsLessonPacks}
                isVirtualCoaching={isVirtualCoaching}
                setIsVirtualCoaching={setIsVirtualCoaching}
                showVirtualCoaching={
                  virtualCoachingEnabled && !isOnboarding && isCreateFlow
                }
                validationErrorHandlerRef={validationErrorHandlerRef}
                isSubmitting={isSubmitting}
                isFormValid={isValid}
                isFormDirty={isFormDirty}
                coachFacilities={coachFacilities}
                coachAvailabilities={coachAvailabilities}
                coachingCenters={coachingCenters}
                durationInMinutesOptions={durationInMinutesOptions}
                categories={categories}
                handleCategoryChange={handleCategoryChange}
                startTimeIncrementOptions={startTimeIncrementOptions}
                paymentAccounts={paymentAccounts}
                singleLessonPrice={singleLessonPrice}
                errors={errors}
                control={control}
                register={register}
                watch={watch}
                status={status}
                setStatus={setStatus}
                giftingEnabled={giftingEnabled}
                setGiftingEnabled={setGiftingEnabled}
                trigger={trigger}
                onCancel={handleCancel}
              />
            </form>
          </FormProvider>
        </MarkdownRteFlushProvider>
        {isCreateFlow ? (
          <BookingTypeFormSuccessDialog
            isOpen={isSuccessDialogOpen}
            setIsOpen={setIsSuccessDialogOpen}
            showOnboardingModal={showOnboardingModal}
            onClose={onSuccessDialogClose}
            onCreateAnother={onSuccessDialogCreateAnother}
            lessonType={lessonTypeSuccessResult ?? {}}
          />
        ) : null}
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={bookingType ? 'Edit booking type' : 'Create booking type'}
        crumbs={crumbs}
      />
      {isOnboarding ? (
        <Box className={settingsStyles.contentContainer}>
          <OnboardingProgressSection
            coachOnboardingProgress={coachOnboardingProgress}
          />
        </Box>
      ) : null}
      <MarkdownRteFlushProvider flushApiRef={markdownRteFlushApiRef}>
        <FormProvider {...form}>
          <form onSubmit={flushMarkdownEditorsAndSubmit(onSubmit)}>
            <FormActionBar
              includeCancel={isEmpty(touched)}
              instructions={hasErrors && 'Form has one or more errors'}
              error={hasErrors}
            >
              {bookingType && !isEmpty(touched) && (
                <Dialog
                  trigger={
                    <Button style={{ marginRight: '16px' }}>Cancel</Button>
                  }
                  maxWidth="xs"
                >
                  {({ closeDialog }) => {
                    return (
                      <>
                        <DialogTitle>Unsaved Changes</DialogTitle>
                        <DialogContent>
                          <Typography variant="p" gutterBottom>
                            If you leave before saving, your changes will be
                            lost. Are you sure you want to leave without saving?
                          </Typography>
                        </DialogContent>
                        <DialogActions>
                          <Button color="primary" onClick={closeDialog}>
                            No
                          </Button>
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={() =>
                              history.push(
                                isOnboarding
                                  ? '/pga-coach'
                                  : getOfferingsPath(
                                    OFFERINGS_TAB.BOOKING_TYPE,
                                  ),
                              )
                            }
                          >
                            Yes
                          </Button>
                        </DialogActions>
                      </>
                    )
                  }}
                </Dialog>
              )}
              <Button
                variant="contained"
                color="primary"
                type="submit"
                data-cy="submit"
                className={classes.submitButton}
                disabled={
                  isCreateLessonTypeSubmitting || isUpdateLessonTypeSubmitting
                }
              >
                {!bookingType
                  ? isLessonPacks
                    ? 'Create Lesson Pack'
                    : 'Create Booking Type'
                  : 'Save'}
              </Button>
            </FormActionBar>
            <Box
              m={isMobile ? 1 : 7}
              mt={isMobile ? 2 : 7}
              style={{ width: 'fit-content' }}
            >
              <Grid container>
                <Grid item xs={12} sm={12}>
                  <Card variant="outlined" className={classes.card}>
                    <CardHeader
                      title="Booking Details"
                      titleTypographyProps={{ variant: 'h6' }}
                      className={classes.header}
                    />
                    <CardContent className={classes.content}>
                      <Box p={isMobile ? 3 : 4} pb={0}>
                        <SingleOrLessonPack
                          isLessonPacks={isLessonPacks}
                          setIsLessonPacks={setIsLessonPacks}
                          paymentAccounts={paymentAccounts}
                        />
                        <BookingTypeDetails
                          coachFacilities={coachFacilities}
                          coachAvailabilities={coachAvailabilities}
                          coachingCenters={coachingCenters}
                          durationInMinutesOptions={durationInMinutesOptions}
                          categories={categories}
                          handleCategoryChange={handleCategoryChange}
                          startTimeIncrementOptions={startTimeIncrementOptions}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" className={classes.card}>
                    <CardHeader
                      title="Payment Options"
                      id="payment-options"
                      titleTypographyProps={{ variant: 'h6' }}
                      className={classes.header}
                    />
                    <CardContent className={classes.content}>
                      <Box p={isMobile ? 3 : 4} pb={0}>
                        <BookingTypePaymentOptions
                          errors={errors}
                          control={control}
                          register={register}
                          watch={watch}
                          isLessonPacks={isLessonPacks}
                          paymentAccounts={paymentAccounts}
                          singleLessonPrice={singleLessonPrice}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" className={classes.card}>
                    <CardHeader
                      title="Additional Options"
                      titleTypographyProps={{ variant: 'h6' }}
                      className={classes.header}
                    />
                    <CardContent className={classes.content}>
                      <Box p={isMobile ? 3 : 4} pt={0}>
                        <BookingTypeAdditionalOptions
                          status={status}
                          setStatus={setStatus}
                          bookingType={bookingType}
                          isLessonPacks={isLessonPacks}
                          giftingEnabled={giftingEnabled}
                          setGiftingEnabled={setGiftingEnabled}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                  <BookingTypeFormSuccessDialog
                    isOpen={isSuccessDialogOpen}
                    setIsOpen={setIsSuccessDialogOpen}
                    showOnboardingModal={showOnboardingModal}
                    onClose={onSuccessDialogClose}
                    onCreateAnother={onSuccessDialogCreateAnother}
                    lessonType={lessonTypeSuccessResult ?? {}}
                  />
                  <Box
                    pt={isMobile ? 2 : 7}
                    pb={isMobile ? 12 : 1}
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                  >
                    {bookingType && (
                      <Dialog
                        trigger={
                          <Button
                            type="button"
                            color="primary"
                            data-cy="delete"
                          >
                            <Typography color="error"> Delete </Typography>
                          </Button>
                        }
                        maxWidth="xs"
                      >
                        {({ closeDialog }) => {
                          const onDeleteBookingTypeConfirm = () => {
                            onDeleteBookingType()
                          }
                          return (
                            <>
                              <DialogTitle>Delete Booking Type</DialogTitle>
                              <DialogContent>
                                <Typography variant="p" gutterBottom>
                                  Lessons that have already been created will
                                  not be impacted.
                                </Typography>
                              </DialogContent>
                              <DialogActions>
                                <Button color="primary" onClick={closeDialog}>
                                  Cancel
                                </Button>
                                <Button
                                  data-cy="confirmDelete"
                                  color="primary"
                                  variant="contained"
                                  onClick={onDeleteBookingTypeConfirm}
                                >
                                  Delete
                                </Button>
                              </DialogActions>
                            </>
                          )
                        }}
                      </Dialog>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </form>
        </FormProvider>
      </MarkdownRteFlushProvider>
    </>
  )
}

export default BookingTypeForm
